import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { Socket } from "socket.io-client";
import { DefaultEventsMap } from "@socket.io/component-emitter";
import { CustomAlert } from "../components/Alert/CustomAlert";
import { connectGovbrCredential, getGovbrConnections, markGovbrConnectionSeen } from "../_services/credential.service";
import { IGovbrConnectionAttempt } from "../_services/interface/credential.interface";
import { mensagemDaValidacaoGovbr } from "../pages/Credentials/components/govbrValidationMessages";
import { escapeHtml } from "../utils/escapeHtml";

/** Evento que o backend emite para quem pediu, quando uma conexão termina. */
export const GOVBR_CONNECTION_EVENT = "govbrConnectionUpdate";

/** Intervalo do polling — só enquanto houver conexão em curso. */
const POLLING_MS = 4000;

/** Por quanto tempo o sucesso continua visível no ícone antes de ele sumir (decisão de 13/10/2026). */
export const SUCESSO_VISIVEL_MS = 10000;

const ATIVOS = ["queued", "running"];

export const emCurso = (a: IGovbrConnectionAttempt) => ATIVOS.includes(a.status);

export const erroNaoVisto = (a: IGovbrConnectionAttempt) => a.status === "failed" && !a.seen_at;

interface IGovbrConnectionContext {
  /** O que o ícone mostra: em curso, erro não visto e sucesso dos últimos segundos. */
  attempts: IGovbrConnectionAttempt[];
  /** Pede a conexão em segundo plano. `null` = o pedido não saiu (o motivo vai em `errorCode`). */
  start: (guidCredential: string) => Promise<{ attempt: IGovbrConnectionAttempt | null; errorCode?: string }>;
  /** Marca os erros como vistos — o badge apaga. */
  markSeen: () => Promise<void>;
  refresh: () => Promise<void>;
}

const GovbrConnectionContext = createContext<IGovbrConnectionContext | undefined>(undefined);

export const useGovbrConnections = () => {
  const context = useContext(GovbrConnectionContext);
  if (!context) {
    throw new Error("useGovbrConnections must be used within a GovbrConnectionProvider");
  }
  return context;
};

interface IGovbrConnectionProviderProps {
  guidClient: string | undefined;
  socket: Socket<DefaultEventsMap, DefaultEventsMap> | undefined;
  children: ReactNode;
}

/**
 * GEN-3754 — estado GLOBAL das conexões gov.br em segundo plano.
 *
 * Mora no `AppShell`, e não na tela de Credenciais, porque o ponto todo do card é sair dela: o
 * cliente pede a conexão e continua navegando, e o ícone da barra superior acompanha de qualquer
 * tela. O estado vem do SERVIDOR (busca ao montar), então recarregar a página não perde nada.
 *
 * Atualização: polling curto SÓ enquanto houver conexão em curso, mais o evento do socket quando
 * o desfecho chega. Sem conexão em curso, nenhuma requisição recorrente.
 */
export const GovbrConnectionProvider = ({ guidClient, socket, children }: IGovbrConnectionProviderProps) => {
  const toast = useToast();
  const [todas, setTodas] = useState<IGovbrConnectionAttempt[]>([]);
  // Relógio para o sucesso sumir do ícone sozinho, sem esperar a próxima busca.
  const [agora, setAgora] = useState(() => Date.now());
  // Conta as buscas TERMINADAS (com ou sem sucesso). É o que reagenda o polling: depender só de
  // `todas` fazia uma busca com erro de rede parar o polling para sempre — o ícone congelava.
  const [ciclo, setCiclo] = useState(0);

  /**
   * Token de descarte. Toda busca nova (e a troca de cliente) invalida as que estão em voo: sem
   * isto, a resposta do cliente ANTERIOR chegava depois da troca e voltava para o ícone, e uma
   * resposta velha ("running") que chegasse depois da nova ("failed") repetia o aviso.
   */
  const seq = useRef(0);

  /** Erros que a pessoa já viu NESTA sessão — o badge não volta se a busca chegar antes do POST. */
  const vistosAqui = useRef<Map<number, string>>(new Map());

  /**
   * Quando o SUCESSO foi visto aqui, pelo relógio local. O sumiço do ícone conta a partir disto,
   * e não do `finished_at` do servidor: misturar os dois relógios fazia o sucesso sumir na hora
   * (ou nunca) com o relógio da máquina adiantado ou atrasado.
   */
  const sucessoVistoEm = useRef<Map<number, number>>(new Map());

  /**
   * O status de cada conexão na última busca. É o que separa "terminou AGORA, nesta sessão" (vale
   * um aviso) de "já tinha terminado quando a página abriu" (não vale — seria repetir aviso a cada F5).
   */
  const anteriores = useRef<Map<number, string>>(new Map());

  const avisar = useCallback(
    (a: IGovbrConnectionAttempt) => {
      const sucesso = a.status === "succeeded";
      const mensagem = mensagemDaValidacaoGovbr(sucesso ? "reconectada" : a.outcome);
      // O rótulo vem do cadastro do cliente e entra em HTML: escapado.
      const rotulo = escapeHtml(a.label);

      toast({
        status: sucesso ? "success" : "error",
        duration: 8000,
        isClosable: true,
        containerStyle: { maxW: "800px" },
        render: ({ onClose }) => (
          <CustomAlert
            closableFunc={onClose}
            descriptionHtml={
              `<div><b>${mensagem.titulo}</b></div>${rotulo}` +
              (sucesso ? "" : " — veja os detalhes no ícone de conexão, na barra superior.")
            }
            icon={true}
            status={sucesso ? "success" : "error"}
            variant={"alert"}
            type={"three"}
            className={"alert-instance"}
          />
        ),
      });
    },
    [toast]
  );

  const aplicar = useCallback(
    (lista: IGovbrConnectionAttempt[]) => {
      for (const a of lista) {
        const antes = anteriores.current.get(a.id);
        if (antes && ATIVOS.includes(antes) && !emCurso(a)) {
          avisar(a);
        }
      }
      anteriores.current = new Map(lista.map((a) => [a.id, a.status]));
      const instante = Date.now();
      for (const a of lista) {
        if (a.status === "succeeded" && !sucessoVistoEm.current.has(a.id)) {
          sucessoVistoEm.current.set(a.id, instante);
        }
      }
      setTodas(
        lista.map((a) =>
          erroNaoVisto(a) && vistosAqui.current.has(a.id)
            ? { ...a, seen_at: vistosAqui.current.get(a.id) as string }
            : a
        )
      );
      setAgora(instante);
    },
    [avisar]
  );

  const refresh = useCallback(async () => {
    if (!guidClient) return;
    const minha = ++seq.current;
    try {
      const { status, response } = await getGovbrConnections(guidClient);
      if (minha !== seq.current) return;
      if (status === 200 && Array.isArray(response)) {
        aplicar(response);
      }
    } finally {
      if (minha === seq.current) setCiclo((c) => c + 1);
    }
  }, [guidClient, aplicar]);

  // Troca de cliente: o que era do outro cliente não pode continuar no ícone.
  useEffect(() => {
    seq.current += 1;
    anteriores.current = new Map();
    vistosAqui.current = new Map();
    sucessoVistoEm.current = new Map();
    setTodas([]);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidClient]);

  const algumaEmCurso = todas.some(emCurso);

  // Reagenda a cada busca terminada, com ou sem sucesso (ver `ciclo`).
  useEffect(() => {
    if (!algumaEmCurso) return undefined;
    const t = setTimeout(() => refresh(), POLLING_MS);
    return () => clearTimeout(t);
  }, [algumaEmCurso, ciclo, refresh]);

  // O handler muda a cada render; a assinatura não pode (ver a nota igual no `AlertsBell`).
  const aoEvento = useRef<() => void>(() => undefined);
  aoEvento.current = () => {
    refresh();
  };

  useEffect(() => {
    if (!socket) return undefined;
    const handler = () => aoEvento.current();
    socket.on(GOVBR_CONNECTION_EVENT, handler);
    return () => {
      socket.off(GOVBR_CONNECTION_EVENT, handler);
    };
  }, [socket]);

  const visiveis = useMemo(
    () =>
      todas.filter(
        (a) => a.status !== "succeeded" || agora - (sucessoVistoEm.current.get(a.id) ?? agora) < SUCESSO_VISIVEL_MS
      ),
    [todas, agora]
  );

  // Agenda o "sumir" do sucesso mais antigo que ainda está visível.
  useEffect(() => {
    const prazos = visiveis
      .filter((a) => a.status === "succeeded")
      .map((a) => (sucessoVistoEm.current.get(a.id) ?? Date.now()) + SUCESSO_VISIVEL_MS - Date.now());
    if (prazos.length === 0) return undefined;
    const t = setTimeout(() => setAgora(Date.now()), Math.max(500, Math.min(...prazos)));
    return () => clearTimeout(t);
  }, [visiveis]);

  const start = useCallback(
    async (guidCredential: string) => {
      if (!guidClient) return { attempt: null, errorCode: "NO_CLIENT" };
      // Invalida as buscas em voo: uma resposta que saiu ANTES deste pedido não conhece a tentativa
      // nova e, chegando depois, apagaria a linha recém-inserida (e o aviso do desfecho junto).
      const epoca = ++seq.current;
      const { status, response } = await connectGovbrCredential(guidClient, guidCredential);
      const corpo = response as { attempt?: IGovbrConnectionAttempt; message?: string } | undefined;
      // A busca que este pedido invalidou não reagenda o polling (ela foi descartada). Quem
      // reagenda é o próprio pedido — no sucesso e no erro —, senão o ícone pararia de andar.
      if (epoca === seq.current) setCiclo((c) => c + 1);
      if ((status === 200 || status === 201 || status === 202) && corpo?.attempt) {
        const tentativa = corpo.attempt;
        // Trocou de cliente durante o POST: a tentativa é do cliente anterior e não entra no ícone.
        if (epoca !== seq.current) return { attempt: tentativa };
        anteriores.current.set(tentativa.id, tentativa.status);
        setTodas((lista) => [tentativa, ...lista.filter((a) => a.id !== tentativa.id)]);
        return { attempt: tentativa };
      }
      return { attempt: null, errorCode: String(corpo?.message ?? status ?? "") };
    },
    [guidClient]
  );

  const markSeen = useCallback(async () => {
    if (!guidClient) return;
    const pendentes = todas.filter(erroNaoVisto);
    if (pendentes.length === 0) return;
    const vistoEm = new Date().toISOString();
    // Otimista: o badge apaga na hora; o servidor confirma em segundo plano. `vistosAqui` impede
    // que uma busca em voo (que ainda não sabe do POST) acenda o badge de novo.
    pendentes.forEach((a) => vistosAqui.current.set(a.id, vistoEm));
    setTodas((lista) => lista.map((a) => (erroNaoVisto(a) ? { ...a, seen_at: vistoEm } : a)));
    await Promise.all(pendentes.map((a) => markGovbrConnectionSeen(guidClient, a.id)));
  }, [guidClient, todas]);

  const valor = useMemo(() => ({ attempts: visiveis, start, markSeen, refresh }), [visiveis, start, markSeen, refresh]);

  return <GovbrConnectionContext.Provider value={valor}>{children}</GovbrConnectionContext.Provider>;
};
